<template>
  <v-row class="fill-height" no-gutters>
    <v-col cols="12">
      <template v-if="isFetched">
        <card-title-row
          :is-floating="!disableTitleFloating"
          :featureTitle="cardSubheader"
          :has-back-button="hasBackButton"
          has-tools-buttons
          @action="backButtonAction"
          class="py-4"
        >
          <template #buttons v-if="!isNew && !treeViewFeature && !isCacheLayer">
            <slot name="buttons">
              <dots-menu
                v-if="!hideDotsItems"
                :items="dotsMenuItems"
                @edit="startEditing('edit')"
                @delete="deleteObjectInit"
              />
            </slot>
          </template>
          <template #toolsButtons v-if="(!isZdmDataScopeLayer && !isNew) || treeViewFeature">
            <slot name="toolsButtons">
              <v-row>
                <base-rounded-button
                  v-if="hasEditPermissions && !isSqlSimpleLocal && !treeViewFeature"
                  :disabled="!formDataRefreshed"
                  :color="isEditingOn ? 'primarybackground' : 'rgba(235, 235, 235, 0.5)'"
                  :iconColor="isEditingOn ? '#1A73E8' : '#202124'"
                  icon="mdi-pencil"
                  size="x-small"
                  :tooltip="isEditingOn ? $i18n.t('sidebar.turnOffEditing') : $i18n.t('sidebar.turnOnEditing')"
                  disable-auto-focus-enter-action
                  v-bind="editingButtonBind"
                  @click="editButtonClickHandler"
                />
                <base-rounded-button
                  v-if="!hideFeatureWatcher"
                  :color="!isFeatureWatched ? 'rgba(235, 235, 235, 0.5)' : 'primarybackground'"
                  :iconColor="!isFeatureWatched ? '#202124' : '#1A73E8'"
                  icon="mdi-eye-outline"
                  size="x-small"
                  @click="handleWatcher"
                  :tooltip="!isFeatureWatched ? $i18n.t('sidebar.observeObject') : $i18n.t('sidebar.unobserveObject')"
                />
                <base-rounded-button
                  v-if="layer.geometry_type"
                  color="rgba(235, 235, 235, 0.5)"
                  iconColor="#202124"
                  icon="mdi-magnify"
                  size="x-small"
                  @click="zoomToFeature"
                  :tooltip="$i18n.t('sidebar.zoomOnMap')"
                />
                <base-rounded-button
                  size="x-small"
                  @click="openZSIDialog"
                  :tooltip="$i18n.t('dialog.external_systems.tooltip')"
                  inner-text="ZSI"
                  color="rgba(235, 235, 235, 0.5)"
                  iconColor="#202124"
                  v-if="isExternalSystemsEnabled && isDatasourceConnectedToExternalSystem"
                />
                <slot name="toolsCustomButtons" />
                <feature-card-show-buttons
                  v-if="!hideObjectExtras && persistOldExtras"
                  :permissions="commentAttachmentsPermissions"
                  @showNotes="showExtrasTab('notes')"
                  @showHistory="showExtrasTab('history')"
                  @showAttachments="showExtrasTab('attachments')"
                  @showRelations="showExtrasTab('relations')"
                  :featureProps="properties"
                  :icons-to-show="iconsToShow"
                />
                <v-badge
                  v-if="isVideoModuleVisible"
                  :content="relationsVideoModuleAmount"
                  :value="relationsVideoModuleAmount > 0"
                  :offset-y="15"
                  :offset-x="15"
                  color="error"
                  overlap
                  bordered
                >
                  <base-rounded-button
                    color="rgba(235, 235, 235, 0.5)"
                    icon="mdi-video"
                    icon-color="#202124"
                    size="x-small"
                    :tooltip="$i18n.t('sidebar.showRelations')"
                    @click="showExtrasTab('relations')"
                  />
                </v-badge>
                <feature-card-pdf-button
                  v-if="!treeViewFeature"
                  :feature-properties="properties"
                  :feature="feature"
                  :layer="layer"
                  :formSchema="formSchema"
                  :file-name="Object.values(cardSubheader).join(': ')"
                />
                <feature-card-multilanguage
                  v-if="isSmartMatchEnabled && isSmartMatchEditEnabled && smartMatchProfile"
                  :dataSource="layerDataSourceName"
                  :formSchema="formSchema"
                  :is-loading="isStickyButtonsLoading"
                  :values.sync="values"
                  :smart-match-profile="smartMatchProfile"
                />
                <base-rounded-button
                  v-if="hasMeasure"
                  :color="isMeasureFeatureActive ? 'primarybackground' : 'rgba(235, 235, 235, 0.5)'"
                  :iconColor="isMeasureFeatureActive ? '#1A73E8' : '#202124'"
                  icon="mdi-ruler"
                  size="x-small"
                  @click="toggleFeatureMeasurement()"
                  :tooltip="
                    isEditingOn
                      ? $i18n.t('button.toChangeMeasurementsVisibility', {
                          reason: $i18n.t('button.turnOffEditing'),
                        })
                      : isMeasureFeatureActive
                      ? $i18n.t('sidebar.hideMeasurements')
                      : $i18n.t('sidebar.showMeasurements')
                  "
                  :loading="measureFeatureLoading"
                  :disabled="isEditingOn"
                />
                <feature-card-form-print-button
                  v-if="isFormPrintButtonVisible"
                  :feature-id="featureId"
                  :current-layer="layer"
                />
              </v-row>
            </slot>
          </template>
          <template #toolsButtons v-else-if="isZdmDataScopeLayer && !isNew">
            <slot name="toolsButtons">
              <v-row>
                <feature-card-zdm-buttons
                  :geometry="geometry"
                  :permissions="commentAttachmentsPermissions"
                  @external_systems="openZSIDialog"
                  :layer-id="+layerId"
                  :feature-id="featureId"
                  :dataSource="layerDataSourceName"
                  :number-attribute-value="numberAttributeValue"
                  :street-attribute-value="streetAttributeValue"
                  :district-attribute-value="districtAttributeValue"
                >
                  <template #prependButtons>
                    <v-col cols="auto">
                      <base-rounded-button
                        v-if="hasEditPermissions && !isSqlSimpleLocal && !treeViewFeature"
                        v-bind="editingButtonBind"
                        @click="editButtonClickHandler"
                      />
                    </v-col>
                  </template>
                </feature-card-zdm-buttons>
              </v-row>
            </slot>
          </template>
        </card-title-row>
        <sidebar-row v-if="layerImageAttributeValues?.length" class="pt-4">
          <template
            v-for="(image, idx) of layerImageGallery ? [layerImageAttributeValues[0]] : layerImageAttributeValues"
          >
            <v-hover :key="idx" v-if="!errorImages.includes(image)">
              <template #default="{ hover }">
                <v-col style="width: 23vw; position: relative" class="d-flex pa-0 pb-1">
                  <img :src="image" style="width: 100%" @error="errorImages.push(image)" />
                  <v-btn
                    v-if="hover"
                    style="position: absolute; top: 3px; right: 3px"
                    class="pa-0"
                    color="rgba(255,255,255,0.5)"
                    height="30"
                    min-width="30"
                    depressed
                    @click="toggleFullScreen(layerImageGallery ? layerImageAttributeValues : [image])"
                  >
                    <v-icon color="rgba(0,0,0,0.6)">mdi-fullscreen</v-icon>
                  </v-btn>
                </v-col>
              </template>
            </v-hover>
          </template>
        </sidebar-row>
        <sidebar-row
          v-if="layerDataSourceName === zdmDataSources?.cable.datasource && !isNew"
          class="pr-5 pt-3"
          :border-bottom="true"
        >
          <zdm-relation-widget :data-source="zdmDataSources.cable.datasource" :featureId="featureId" class="pb-2" />
        </sidebar-row>
        <v-row no-gutters v-if="isInspectionsVisible && !isNew">
          <v-col cols="12">
            <feature-card-inspections-widget
              :layer-id="+layerId"
              :feature-id="+featureId"
              :is-feature-editing="isEditingOn"
              :values="properties"
            />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" style="max-width: 100%">
            <slot name="top" v-bind="$props"> </slot>
          </v-col>
        </v-row>
        <template v-if="persistOldExtras">
          <v-form ref="featureCardDataForm" v-model="isFormValid">
            <feature-card-data
              :elements="formSchema"
              v-model="values"
              :is-editing-on="isEditingOn && formDataRefreshed"
              :layer-id="layerId"
              arrows-mode="on-with-storage"
              :geometry="geometry"
              @input="onValuesInput"
            />
          </v-form>
          <object-extras
            ref="extras"
            v-if="!isNew && !hideObjectExtras"
            :dataSourceName="layerDataSourceName"
            :featureId="featureId"
            :layerId="layerId"
            persist-old-extras
            :permissions="commentAttachmentsPermissions"
            :tabs="objectExtrasTabs"
            :geometry="geometry"
          />
        </template>
        <template v-else>
          <object-extras
            ref="extras"
            :dataSourceName="layerDataSourceName"
            :are-extras-hidden="isNew || hideObjectExtras"
            :featureId="featureId"
            :featureProps="properties"
            :layerId="layerId"
            :permissions="commentAttachmentsPermissions"
            :tabs="objectExtrasTabs"
            :geometry="geometry"
          >
            <template #attributes>
              <v-form ref="featureCardDataForm" v-model="isFormValid">
                <feature-card-data
                  :elements="formSchema"
                  v-model="values"
                  :is-editing-on="isEditingOn && formDataRefreshed"
                  :layer-id="layerId"
                  arrows-mode="on-with-storage"
                  :geometry="geometry"
                  @input="onValuesInput"
                />
              </v-form>
            </template>
          </object-extras>
        </template>
      </template>
      <main-loader v-else class="pt-10" />
      <main-dialog
        :is-visible.sync="isDeleteDialogVisible"
        v-if="isDeleteDialogLoaded"
        :title="$i18n.t('dialog.title.deleteObject')"
        :body="deleteBody"
        agree-text="dialog.agreeDelete"
        @agree="deleteObjectHandler"
        :loading="agreeButtonLoading"
      />
      <external-systems-dialog
        :is-visible.sync="isZSIDialogVisible"
        v-if="isZSIDialogLoaded"
        :feature-id="featureId"
        :system-id="datasourceSystemId"
        :datasource="layerDataSourceName"
      />
      <main-dialog
        v-if="isStopEditingDialogLoaded"
        :is-visible="isStopEditingDialogVisible"
        @update:isVisible="isStopEditingDialogVisible = false"
        :title="$i18n.t('dialog.title.unsavedChanges')"
        :body="$i18n.t('dialog.body.unsavedChangesConfirmation')"
        agree-text="dialog.agreeUnsavedChanged"
        @agree="cancelEditing()"
      />
    </v-col>
    <image-lightbox :items="currentFullScreenImages" :visible.sync="isFullScreenImageVisible" />
  </v-row>
</template>

<script>
import { get, call, sync } from 'vuex-pathify';
import { cloneDeep, debounce } from 'lodash';
import geometryTypeMapping from '@/mixins/map/geometryTypeMapping';

import defaultValues from '@/mixins/defaultValues';
import deleteObject from '@/mixins/deleteObject';
import featureCardHighlight from '@/mixins/featureCardHighlight';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import featureCardZdmButtonsMixin from '@/mixins/featureCardZdmButtonsMixin';
import measurements from '@/mixins/map/measurements';
import styles from '@/mixins/map/styles';
import topo from '@/mixins/topo';

import { prepareDrawnGeometry } from '@/assets/js/mapUtils';
import CardTitleRow from '@/components/CardTitleRow';
import MainLoader from '@/components/MainLoader';
import ObjectExtras from '@/components/ObjectExtras';
import FeatureCardData from '@/components/FeatureCardData';
import DotsMenu from '@/components/DotsMenu';
import FeatureCardZdmButtons from '@/components/FeatureCardZdmButtons';
import FeatureCardShowButtons from '@/components/FeatureCardShowButtons';

import ZdmRelationWidget from '@/components/ZdmRelationWidget';
import SidebarRow from '@/components/SidebarRow';
import FeatureCardPdfButton from '@/components/FeatureCardPdfButton';
import FeatureCardMultilanguage from '@/components/FeatureCardMultilanguage';
import FeatureCardInspectionsWidget from '@/components/FeatureCardInspectionsWidget';
import FeatureCardFormPrintButton from '@/components/FeatureCardFormPrintButton';
import { GeoJSON } from 'ol/format';
import Feature from 'ol/Feature';

import { Style, Fill, Stroke, Circle } from 'ol/style';

export default {
  name: 'FeatureCardBasic',
  mixins: [
    defaultValues,
    deleteObject,
    featureCardHighlight,
    prepareAttributesFeatureCardMixin,
    featureCardZdmButtonsMixin,
    measurements,
    styles,
    geometryTypeMapping,
    topo,
  ],
  components: {
    ExternalSystemsDialog: () => import('@/components/ExternalSystemsDialog'),
    ObjectExtras,
    CardTitleRow,
    MainLoader,
    FeatureCardData,
    DotsMenu,
    FeatureCardZdmButtons,
    ZdmRelationWidget,
    SidebarRow,
    FeatureCardShowButtons,
    MainDialog: () => import('@/components/MainDialog'),
    FeatureCardPdfButton,
    ImageLightbox: () => import('@/components/ImageLightbox'),
    FeatureCardMultilanguage,
    FeatureCardInspectionsWidget,
    FeatureCardFormPrintButton,
  },
  props: {
    title: {
      type: String,
      default: null,
    },
    layer: {
      type: Object,
      required: true,
    },
    treeViewFeature: {
      type: Object,
    },
    updateTreeViewAfterEdit: {
      type: Function,
      default: () => {},
    },
    objectExtrasTabs: {
      type: Array,
    },
    customAddNewObjectAction: {
      type: Function,
    },
    customIsFormValid: {
      type: Boolean,
      default: true,
    },
    iconsToShow: {
      type: Array,
      default: () => ['showHistory', 'showNotes', 'showAttachments', 'showRelations'],
    },
    hideObjectExtras: {
      type: Boolean,
      default: false,
    },
    hideFeatureWatcher: {
      type: Boolean,
      default: false,
    },
    hideDotsItems: {
      type: Boolean,
      default: false,
    },

    customFeatureId: {
      type: [Number, String],
    },
    customLayerId: {
      type: Number,
    },
    disableTitleFloating: {
      type: Boolean,
      default: false,
    },
    isDialogFeatureCard: {
      type: Boolean,
      default: false,
    },
    persistOldExtras: {
      type: Boolean,
      default: false,
    },
    formConstantValues: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data: () => ({
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isZSIDialogVisible: false,
    isZSIDialogLoaded: false,
    isFetched: false,
    formSchema: [],
    isFormValid: true,
    values: {},
    isEditingOn: false,
    geometry: undefined,
    formDataRefreshed: true,
    geometryChanged: false,
    errorImages: [],
    isFullScreenImageVisible: false,
    currentFullScreenImages: [],
    properties: {},
    isMeasureFeatureActive: false,
    measureFeatureLoading: false,
    isStopEditingDialogLoaded: false,
    isStopEditingDialogVisible: false,
    currentCardTab: 0,
  }),
  computed: {
    additionalAttributesValues: sync('layers/additionalAttributesValues'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    isModifyMultipartActive: sync('edit/isModifyMultipartActive'),
    hasUnsavedChanges: sync('sidebar/hasUnsavedChanges'),
    modalOverlayLevel: get('tools/modalOverlayLevel'),
    isKeyBindingActive: get('tools/isKeyBindingActive'),
    isEnterPressed: get('tools/isPressed@enter'),
    // TODO - move it to props
    commentAttachmentsPermissions: get('users/layersPermissions@:layerId.attachments_and_comments_value'),
    layerName() {
      return this.layer?.name;
    },
    layerFormSchema() {
      return this.layer?.form_schema.elements;
    },
    layerDataSourceName() {
      return this.layer?.data_source_name;
    },
    idPropertyName: get('layers/metadata@:layerDataSourceName.attributes_schema.id_name'),
    geometryPropertyName: get('layers/metadata@:layerDataSourceName.attributes_schema.geometry_name'),
    layerAttributesMetadata: get('layers/metadata@:layerDataSourceName.attributes_schema.attributes'),
    geometryReadOnly() {
      return (
        this.layerAttributesMetadata?.find(attribute => attribute.name === this.geometryPropertyName)?.read_only ||
        false
      );
    },
    currentTab: sync('sidebar/currentTab'),
    newFeatureGeometry: sync('edit/newFeatureGeometry'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    stickyButtons: sync('sidebar/stickyButtons'),
    isEditingButtonActive: sync('edit/isEditingButtonActive'),
    isAddingButtonActive: sync('edit/isAddingButtonActive'),
    isCopyButtonActive: sync('edit/isCopyButtonActive'),
    isAssignParcelButtonActive: sync('edit/isAssignParcelButtonActive'),
    copiedObject: get('edit/copiedObject'),
    tableSelectionType: get('edit/tableSelectionType'),
    watermetersLayerId: get('admin/modulesMapping@billing.layer_id'),
    dmaLayerId: get('admin/modulesMapping@billing.dma_layer_id'),
    permissions: get('users/layersPermissions'),
    isDatasourceConnectedToExternalSystem: get('externalSystems/connectedDataSources@:layerDataSourceName'),
    isExternalSystemsEnabled: get('admin/settingsValues@external_systems_module_enabled'),
    zdmDataSources: get('admin/modulesMapping@zdm_data.datasources'),
    isFeatureWatched: sync('watcher/isFeatureWatched'),
    layerRelations: get('relations/relations@:layerDataSourceName'),
    videoModuleDataSourceName: get('admin/modulesMapping@video.selected_datasource_name'),
    isVideoModuleSuperAdminEnabled: get('admin/additionalModules@VIDEO_MODULE.enabled'),
    isVideoModuleAdminEnabled: get('admin/settingsValues@video_module_enabled'),
    isVideoModulePermission: get('users/toolsPermissions@VIDEO_MODULE.main_value'),
    isSmartMatchEnabled: get('admin/settingsValues@smart_match_module_enabled'),
    isSmartMatchEditEnabled: get('admin/additionalModules@SMART_MATCH_EDIT_MODULE.enabled'),
    measuredFeatures: get('map/measuredFeatures'),
    isInspectionsModuleSuperAdminEnabled: get('admin/additionalModules@INSPECTIONS_MODULE.enabled'),
    isInspectionsModuleAdminEnabled: get('admin/settingsValues@inspections_module_enabled'),
    isInspectionsModulePermission: get('users/toolsPermissions@INSPECTIONS_MODULE.main_value'),
    inspectionsLayersIds: get('admin/modulesMapping@inspections.layers'),
    circuitLayerId: get('admin/modulesMapping@zdm_data.circuits.layer_id'),
    isInspectionsVisible() {
      return (
        this.isInspectionsModuleSuperAdminEnabled &&
        this.isInspectionsModuleAdminEnabled &&
        this.isInspectionsModulePermission &&
        (this.inspectionsLayersIds || []).includes(+this.layerId)
      );
    },
    formPrints: get('formPrint/formPrints'),
    isFormPrintLayer() {
      return this.formPrints.some(form => form.layer === this.layer.id);
    },
    isFormPrintSuperAdminEnabled: get('admin/additionalModules@FORM_PRINT.enabled'),
    isFormPrintAdminEnabled: get('admin/settingsValues@form_print_module_enabled'),
    isFormPrintPermission: get('users/toolsPermissions@FORM_PRINT.main_value'),
    isFormPrintButtonVisible() {
      return (
        this.isFormPrintLayer &&
        this.isFormPrintSuperAdminEnabled &&
        this.isFormPrintAdminEnabled &&
        !!this.isFormPrintPermission
      );
    },
    hasMeasure() {
      return this.layer.geometry_type?.includes('polygon') || this.layer.geometry_type?.includes('linestring');
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    isCacheLayer() {
      return (this.projectLayers || []).find(layer => layer.id == this.layerId)?.cache || false;
    },
    isVideoModuleVisible() {
      return (
        this.isVideoModuleSuperAdminEnabled &&
        this.isVideoModuleAdminEnabled &&
        this.isVideoModulePermission &&
        this.layerDataSourceName !== this.videoModuleDataSourceName &&
        this.isGeometricalLayer
      );
    },
    relationsVideoModuleAmount() {
      return this.layerRelations
        ? this.layerRelations[this.featureId]?.filter(
            relation => relation.data_source_name === this.videoModuleDataSourceName
          )?.length
        : 0;
    },
    isZdmDataScopeLayer() {
      return this.layer.layer_scope === 'zdm_data';
    },
    datasourceSystemId() {
      return this.isDatasourceConnectedToExternalSystem?.id;
    },
    isNewCard: sync('map/isNewCard'),
    currentRouteName() {
      return this.$route.name;
    },
    customRoutesLayersIds() {
      return {
        legalizationInfo: this.watermetersLayerId,
        waterDissolutionWatermetersInfo: this.watermetersLayerId,
        waterDissolutionDmaInfo: this.dmaLayerId,
        billingAnomalyDetectionInfo: this.watermetersLayerId,
      };
    },
    customRoutesParentsNames() {
      return {
        legalizationInfo: 'legalization',
        waterDissolutionWatermetersInfo: 'waterDissolutionWatermeters',
        waterDissolutionDmaInfo: 'waterDissolutionDma',
        billingAnomalyDetectionInfo: 'billingAnomalyDetection',
      };
    },
    hasBackButton() {
      return this.customRoutesLayersIds[this.currentRouteName] ? true : false;
    },
    routeParams() {
      return this.$route.params;
    },
    featureId() {
      if (this.customFeatureId || this.customFeatureId === 0) {
        return this.customFeatureId;
      }
      return this.routeParams.fid;
    },
    layerId() {
      if (this.customLayerId || this.customLayerId === 0) {
        return this.customLayerId;
      }
      return this.routeParams?.lid || this.layer?.id;
    },
    routeQuery() {
      return this.$route.query;
    },
    treeViewFeatureId() {
      return this.routeQuery.fid;
    },
    treeViewLayerId() {
      return this.routeQuery.lid;
    },
    computedTreeViewLidFid() {
      return {
        fid: this.treeViewFeatureId,
        lid: this.treeViewLayerId,
      };
    },
    layers: get('layers/layers'),
    treeViewLayerFormSchema: get('layers/layers@:treeViewLayerId@form_schema.elements'),
    treeViewLayerDataSource: get('layers/layers@:treeViewLayerId@data_source_name'),
    treeViewLayerGeometryType: get('layers/layers@:treeViewLayerId@geometry_type'),
    treeViewAttributesMetadata: get('layers/metadata@:treeViewLayerDataSource.attributes_schema.attributes'),
    treeViewIdPropertyName: get('layers/metadata@:treeViewLayerDataSource.attributes_schema.id_name'),
    treeViewGeometryPropertyName: get('layers/metadata@:treeViewLayerDataSource.attributes_schema.geometry_name'),
    hasTreeViewFeature() {
      return this.treeViewFeatureId && this.treeViewLayerId && this.treeViewFeature;
    },
    editType() {
      return this.$route.query.editType;
    },
    cardSubheader() {
      if (this.title) {
        return { title: this.title };
      }
      if (this.isNew) {
        return {
          title: this.$i18n.t('sidebar.newObject'),
          subtitle: this.layerName,
        };
      }
      return {
        title: this.values[this.metadata[this.layerDataSourceName].attributes_schema.desc_attribute_name],
        subtitle: this.layerName,
      };
    },
    computedLidFid() {
      return {
        fid: this.featureId,
        lid: this.layerId,
      };
    },
    isNew() {
      if (this.hasTreeViewFeature) {
        return this.treeViewFeatureId === 'new';
      }
      return this.featureId === 'new';
    },
    hasEditPermissions() {
      return this.permissions && this.permissions[this.layerId]
        ? this.permissions[this.layerId].main_value === 2
        : false;
    },
    smartMatchProfile() {
      return this.layer?.smart_match || null;
    },
    deleteBody() {
      return this.$i18n.t('dialog.body.deleteObject', { id: this.featureId });
    },
    isSqlSimpleLocal() {
      return this.metadata[this.currentLayerDataSource]?.datasource_type === 'sql_simple_local';
    },
    dotsMenuItems() {
      return [
        {
          name: 'delete',
          disabled: !this.hasEditPermissions || !this.formDataRefreshed,
        },
      ];
    },
    isGeometricalLayer() {
      return this.hasTreeViewFeature ? Boolean(this.treeViewLayerGeometryType) : Boolean(this.layer.geometry_type);
    },
    isTopo() {
      return this.isTopologicalLayer(this.layers[this[this.hasTreeViewFeature ? 'treeViewLayerId' : 'layerId']]);
    },
    numberAttributeName() {
      return this.$_objectToArray(this.zdmDataSources || {}).find(d => d.datasource === this.layer.data_source_name)
        ?.display_attribute_name;
    },
    streetAttributeName() {
      return this.$_objectToArray(this.zdmDataSources || {})?.find(d => d.datasource === this.layer.data_source_name)
        ?.street_attribute_name;
    },
    districtAttributeName() {
      return this.$_objectToArray(this.zdmDataSources || {})?.find(d => d.datasource === this.layer.data_source_name)
        ?.district_attribute_name;
    },
    numberAttributeValue() {
      return this.values[this.numberAttributeName];
    },
    streetAttributeValue() {
      return this.values[this.streetAttributeName];
    },
    districtAttributeValue() {
      return this.values[this.districtAttributeName];
    },
    computedIsValid() {
      return this.isFormValid && this.customIsFormValid;
    },
    newTopoIndexes: sync('edit/newTopoIndexes'),
    deletedTopoIndexes: sync('edit/deletedTopoIndexes'),
    layerImageGallery() {
      return this.layer?.as_gallery || false;
    },
    layerImageAttributes() {
      const imageAttributes = JSON.parse(JSON.stringify(this.layer?.image_attributes || []));
      const flattenFormSchema = this.$_flattenStructure(this.formSchema);
      return imageAttributes.sort((a, b) => {
        return (
          flattenFormSchema.findIndex(attr => attr.attribute === a) -
          flattenFormSchema.findIndex(attr => attr.attribute === b)
        );
      });
    },
    token: get('authentication/token'),
    layerImageAttributeValues() {
      return this.layerImageAttributes
        .map(attribute => {
          let imgUrl = this.properties[attribute];
          if (imgUrl?.startsWith('/')) {
            imgUrl = `${process.env.VUE_APP_API_URL}${imgUrl}`;
          }
          return imgUrl
            ? `${imgUrl}${imgUrl.startsWith(process.env.VUE_APP_API_URL) ? `?token=${this.token}` : ''}`
            : '';
        })
        .filter(url => !!url);
    },
    editingButtonBind() {
      return {
        tooltip: this.isEditingOn ? this.$i18n.t('sidebar.turnOffEditing') : this.$i18n.t('sidebar.turnOnEditing'),
        color: this.isEditingOn ? 'primarybackground' : 'rgba(235, 235, 235, 0.5)',
        icon: 'mdi-pencil',
        iconColor: this.isEditingOn ? '#1A73E8' : '#202124',
        size: 'x-small',
        disabled: !this.formDataRefreshed,
      };
    },
  },
  watch: {
    computedLidFid: {
      immediate: true,
      deep: true,
      handler(nV, oV) {
        if (oV?.fid === nV?.fid && oV?.lid === nV?.lid) {
          return;
        }
        this.isFetched = false;
        this.$nextTick(() => {
          this.onLidFidChanged(nV, oV);
        });
      },
    },
    computedTreeViewLidFid: {
      immediate: true,
      deep: true,
      handler(nV, oV) {
        if (oV?.fid === nV?.fid && oV?.lid === nV?.lid) {
          return;
        }
        this.$nextTick(() => {
          this.onTreeViewLidFidChanged(nV, oV);
        });
      },
    },
    computedIsValid: {
      immediate: true,
      handler(nV) {
        this.isStickyButtonsValid = nV;
      },
    },
    featureId: {
      immediate: true,
      async handler(nV) {
        this.isFeatureWatched = false;
        if (!this.isNew && !this.hideFeatureWatcher) {
          await this.checkWatching({ datasource: this.layerDataSourceName, object_id: nV });
        }
      },
    },
    isEditingOn: {
      immediate: true,
      handler(nV) {
        this.$emit('update:isEditingOn', nV);
        if (!this.isNew && nV) {
          this.$refs['featureCardDataForm']?.validate();
        } else if (!nV) {
          this.$refs['featureCardDataForm']?.resetValidation();
        }
      },
    },
    layerImageAttributeValues: {
      handler(nV) {
        if (nV?.length) {
          this.errorImages = [];
        }
      },
      deep: true,
    },
    values: {
      deep: true,
      handler() {
        if (this.isEditingOn) {
          this.hasUnsavedChanges = true;
        }
      },
    },
    additionalAttributesValues: {
      handler(nV) {
        if (nV && this.isFetched) {
          this.values = { ...this.values, ...nV };
          this.additionalAttributesValues = undefined;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    getFormPrints: call('formPrint/getFormPrints'),
    toggleFullScreen(images) {
      this.isFullScreenImageVisible = true;
      this.currentFullScreenImages = images;
    },
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    getLayerFeature: call('layers/getLayerFeature'),
    addNonTopoFeatureAction: call('layers/addDatasourceFeature'),
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    addTopoFeatureAction: call('layers/addTopoDatasourceFeature'),
    editTopoFeatureAction: call('layers/editTopoDatasourceFeature'),
    checkDatasourceConnection: call('externalSystems/checkDatasourceConnection'),
    addWatching: call('watcher/addWatching'),
    stopWatching: call('watcher/stopWatching'),
    checkWatching: call('watcher/checkWatching'),
    getRelations: call('relations/getRelations'),
    handleWatcher() {
      const payload = { datasource: this.layerDataSourceName, object_id: this.featureId };
      this.isFeatureWatched ? this.stopWatched(payload) : this.addToWatched(payload);
    },
    async addToWatched(payload) {
      await this.addWatching(payload);
    },
    async stopWatched(payload) {
      await this.stopWatching(payload);
    },
    refreshFormData() {
      this.updateAfterEdit(true);
    },
    backButtonAction() {
      this.$router.push({ name: this.customRoutesParentsNames[this.currentRouteName] });
    },
    zoomToFeature() {
      const { crs, geometry } = this.feature;
      this.$root.$emit('createGeomAndFitView', { ...geometry, crs });
    },
    pushToParentRoute() {
      this.$router.push({
        name: this.$route.meta.parent,
        params: {
          ...this.$route.params,
          lid: this.layerId,
        },
      });
      this.currentTab = 0;
    },
    setProperties(feature) {
      this.$emit('update:feature', feature);
      this.properties = this.getFlattenProperties(
        feature,
        this.hasTreeViewFeature ? this.treeViewIdPropertyName : this.idPropertyName
      );
    },
    async getFeature(lid = this.layerId, fid = this.featureId) {
      try {
        this.feature = this.hasTreeViewFeature
          ? this.treeViewFeature
          : await this.getLayerFeature({ layer_id: lid, feature_id: fid });
        this.setProperties(this.feature);
        this.geometry = { ...this.feature.geometry, crs: this.feature.crs };
        if (this.isEditingButtonActive) {
          this.startEditing('edit');
        }
      } catch (e) {
        this.pushToParentRoute();
        console.log(e);
      }
    },
    onNewFid() {
      if (this.isGeometricalLayer && !this.newFeatureGeometry && !this.hasTreeViewFeature) {
        this.pushToParentRoute();
      } else {
        this.feature = {
          ...(this.isGeometricalLayer && !this.hasTreeViewFeature ? { geometry: this.newFeatureGeometry } : {}),
        };
        this.geometry = this.isGeometricalLayer && !this.hasTreeViewFeature ? this.newFeatureGeometry : undefined;

        if (this.hasTreeViewFeature) {
          this.properties =
            this.copiedObject || this.getFlattenProperties(this.treeViewFeature, this.treeViewIdPropertyName) || {};
          this.properties[this.treeViewGeometryPropertyName] = this.treeViewFeature.geometry;
          delete this.properties[this.treeViewIdPropertyName];
        } else {
          this.properties = this.copiedObject || {};
        }
        this.startEditing(this.copiedObject ? 'copy' : 'add');
      }
    },
    async updateAfterEdit(onlyRefresh = false) {
      try {
        this.updateMeasure();
        if (this.hasTreeViewFeature) {
          await this.updateTreeViewAfterEdit(true);
        }
        this[onlyRefresh ? 'formDataRefreshed' : 'isFetched'] = false;
        await this.getFeature();
        this.values = { ...this.getValues(this.formSchema, this.properties), ...this.formConstantValues };
        this[onlyRefresh ? 'formDataRefreshed' : 'isFetched'] = true;
      } catch (e) {
        console.log(e);
      }
    },
    async onTreeViewLidFidChanged() {
      if (this.isEditingOn) {
        this.stopEditing();
      }
      if (this.isNew) {
        this.onNewFid();
      } else {
        await this.getFeature();
      }
      this.formSchema = this.hasTreeViewFeature
        ? this.getForm(this.treeViewLayerFormSchema, this.treeViewAttributesMetadata, {
            filteredOutAttributes: Object.keys(this.formConstantValues),
          })
        : this.getForm(this.layerFormSchema, this.layerAttributesMetadata, {
            filteredOutAttributes: Object.keys(this.formConstantValues),
          });
      this.values = { ...this.getValues(this.formSchema, this.properties), ...this.formConstantValues };
      if (!this.isNew && this.isNewCard) {
        this.isNewCard = false;
        if (this.isGeometricalLayer) {
          this.zoomToFeature();
        }
      }
    },
    async onLidFidChanged(nV = this.computedLidFid, oV) {
      this.isMeasureFeatureActive = false;
      if (!this.mapPanelDataFetched) {
        return;
      }
      try {
        if (this.isEditingOn) {
          this.stopEditing();
        }
        this.isFetched = false;
        this.clearHighlitedFeatureHandler(nV.lid, oV?.lid);
        if (this.isNew) {
          this.onNewFid();
        } else {
          if (this.isVideoModuleVisible) {
            const payload = {
              data_source_name: this.layerDataSourceName,
              feature_id: nV.fid,
            };
            await this.getRelations(payload);
          }
          await this.getFeature(nV.lid, nV.fid);
          // this.feature.id instead of nV.fid as this.$route.params.fid may be String instead of Number (usually happens on app init)
          this.highlightFeature(nV.lid, this.feature.id);
        }
        this.formSchema = this.hasTreeViewFeature
          ? this.getForm(this.treeViewLayerFormSchema, this.treeViewAttributesMetadata, {
              filteredOutAttributes: Object.keys(this.formConstantValues),
            })
          : this.getForm(this.layerFormSchema, this.layerAttributesMetadata, {
              filteredOutAttributes: Object.keys(this.formConstantValues),
            });
        this.values = { ...this.getValues(this.formSchema, this.properties), ...this.formConstantValues };
        if (this.additionalAttributesValues) {
          this.values = { ...this.values, ...this.additionalAttributesValues };
          this.additionalAttributesValues = undefined;
        }
        this.valuesCopy = cloneDeep(this.values);
        this.isFetched = true;
        if (!this.isNew && this.isNewCard) {
          this.isNewCard = false;
          if (this.isGeometricalLayer) {
            this.zoomToFeature();
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    startEditing(type) {
      this.hasUnsavedChanges = false;
      this.unsavedChangesNext = undefined;
      this.geometryChanged = false;
      if (!this.isNew) {
        this.featureCopy = cloneDeep(this.feature);
        this.valuesCopy = cloneDeep(this.values);
        this.geometryCopy = cloneDeep(this.geometry);
      }
      const dict = {
        add: 'isAddingButtonActive',
        copy: 'isCopyButtonActive',
        edit: 'isEditingButtonActive',
      };
      this[dict[type]] = true;
      this.isEditingOn = true;
      if (this.isDialogFeatureCard) {
        this.$emit('featureEditing');
      } else {
        this.isStickyButtonsVisible = true;
        this.stickyButtons = [
          {
            action: this.isNew ? 'featureAdding' : 'featureEditing',
            translationPath: this.isNew ? 'dialog.agreeAdd' : 'dialog.save',
          },
          {
            action: 'cancelEditing',
            translationPath: 'dialog.disagree',
            cancel: true,
          },
        ];
      }
      this.$root.$emit('selection-action');
      if (!this.isGeometricalLayer || this.geometryReadOnly) {
        return;
      }
      this.toggleFeatureMeasurement(false, true);
      this.modifyFeatureGeometry();
    },
    modifyFeatureGeometry() {
      this.$root.$emit('modifySidebarGeometry', {
        geometryValue: this.geometry,
        modifyendCallback: this.onModifyend,
        layerId: this.hasTreeViewFeature ? this.treeViewLayerId : this.layerId,
        ...(this.isMeasureFeatureActive ? { styleFunction: this.getMeasureFeatureStyleFunction() } : {}),
        isInitSnapping: true,
      });
    },
    multipartFeatureManagement(tool) {
      if (!this.isEditingOn) throw Error('Editing mode not enabled.');
      this.isModifyMultipartActive = !!tool;
      if (!tool) {
        this.$root.$emit('deleteDrawModifyInteractions');
        this.modifyFeatureGeometry();
        return;
      }
      switch (tool) {
        case 'add_part':
          return this.addFeatureMultipart('default');
        case 'delete_part':
          return this.deleteFeatureMultipart();
        case 'add_part_parcel':
          return this.addFeatureMultipart('parcel');
      }
    },
    addFeatureMultipart(type) {
      this.$root.$emit('toggleAddMultipartGeometry', {
        type,
        params: {
          geometryType: this.getOlGeometrySimpleType(this.geometry.type),
          callback: this.onDrawend,
          layerId: this.hasTreeViewFeature ? this.treeViewLayerId : this.layerId,
        },
      });
    },
    deleteFeatureMultipart() {
      this.$root.$emit('toggleRemoveMultipartGeometry', {
        geometryValue: this.geometry,
        selectCallback: this.onDeleteSelectEnd,
      });
    },
    onDeleteSelectEnd({ newGeometry }) {
      this.isModifyMultipartActive = false;
      this.geometryChanged = this.hasUnsavedChanges = !!newGeometry;
      if (!newGeometry) return;
      this.setSidebarGeometry(newGeometry);
      this.modifyFeatureGeometry();
    },
    onDrawend(e) {
      this.geometryChanged = this.hasUnsavedChanges = true;
      this.isModifyMultipartActive = false;
      const wholeGeometry = new GeoJSON().readGeometry(this.geometry, {
        dataProjection: 'EPSG:3857',
        featureProjection: 'EPSG:3857',
      });
      wholeGeometry[`append${this.getOlGeometrySimpleType(this.geometry.type)}`](e.feature.getGeometry());
      this.setSidebarGeometry(new Feature({ geometry: wholeGeometry }));
      this.modifyFeatureGeometry();
    },
    getMeasureFeatureStyleFunction() {
      return feature => {
        const geometry = feature.getGeometry();
        const styleFunction = this.typeStyleFunctionsDist[geometry?.getType()];
        return [
          new Style({
            fill: new Fill({
              color: 'rgba(200,0,0,0.6)',
            }),
            stroke: new Stroke({
              color: 'rgba(255,0,0,1)',
              width: 2,
            }),
            image: new Circle({
              radius: 5,
              stroke: new Stroke({
                color: 'rgba(0, 0, 255, 1)',
              }),
              fill: new Fill({
                color: 'rgba(63, 127, 191, 0.7)',
              }),
            }),
          }),
          ...this[styleFunction]({ geometry, simple: true }),
        ];
      };
    },
    cancelEditing() {
      this.stopEditing();
      if (this.hasTreeViewFeature && this.isNew) {
        this.$emit('cancelTreeViewElementAdding');
        this.isAddingButtonActive = false;
        return;
      } else if (this.isNew) {
        this.pushToParentRoute();
        this.isAddingButtonActive = false;
        return;
      }
      if (this.isMeasureFeatureActive) {
        this.toggleFeatureMeasurement(true);
      }
      this.feature = cloneDeep(this.featureCopy);
      this.values = cloneDeep(this.valuesCopy);
      this.geometry = cloneDeep(this.geometryCopy);
    },
    openStopEditingDialog() {
      this.isStopEditingDialogLoaded = true;
      this.isStopEditingDialogVisible = true;
    },
    stopEditing(selection = true) {
      this.clearButtons();
      // this.isCopyButtonActive = false;
      if (selection) {
        this.$root.$emit('selection-action', this.tableSelectionType);
      }
      this.$root.$emit('deleteSidebarGeometry');
    },
    clearButtons() {
      this.hasUnsavedChanges = false;
      this.unsavedChangesNext = undefined;
      this.isStopEditingDialogVisible = false;
      this.isEditingOn = false;
      this.isStickyButtonsVisible = false;
      this.isStickyButtonsLoading = false;
      this.stickyButtons = [];
      this.isEditingButtonActive = false;
      this.isAddingButtonActive = false;
      this.isAssignParcelButtonActive = false;
    },
    setSidebarGeometry(feature) {
      if (!feature) {
        this.geometry = null;
      } else {
        this.geometry = prepareDrawnGeometry(feature);
      }
    },
    onModifyend(e) {
      this.geometryChanged = true;
      this.hasUnsavedChanges = true;
      this.setSidebarGeometry(e.features.getArray()[0]);
    },
    handleFeatureAdding() {
      if (this.isTopo) {
        this.addTopoFeature();
      } else {
        this.addNonTopoFeature();
      }
    },
    handleFeatureEditing() {
      if (this.isTopo) {
        this.editTopoFeature();
      } else {
        this.editNonTopoFeature();
      }
    },
    pushMessage(message) {
      this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
        message,
      });
    },
    async editingAddingHof(fn, payload, message) {
      try {
        this.isStickyButtonsLoading = true;
        const r = await fn(payload);
        this.stopEditing();
        this.pushMessage(message);
        this.isStickyButtonsLoading = false;
        return r;
      } catch (error) {
        this.cancelEditing();
        console.log(error);
        throw new Error(error);
      }
    },
    async pushRouter(fid) {
      if (this.hasTreeViewFeature) {
        await this.$router.push({
          name: this.$route.name,
          params: this.$route.params,
          query: { ...this.$route.query, fid },
        });
        await this.updateTreeViewAfterEdit(false, true);
      } else {
        await this.$router.push({ name: this.$route.name, params: { ...this.$route.params, fid } });
      }
    },
    async addingHof(fn, payload) {
      const r = await this.editingAddingHof(fn, payload, this.$i18n.t('success.successAddedObject'));
      if (this.isCopyButtonActive) {
        this.$root.$emit('turnOnCopying');
      }
      return r;
    },
    async addNonTopoFeature() {
      const datasource = this.hasTreeViewFeature ? this.treeViewLayerDataSource : this.layerDataSourceName;
      const action = this.customAddNewObjectAction || this.addNonTopoFeatureAction;
      const r = await this.addingHof(action, {
        datasource,
        body: this.getPayload(this.hasTreeViewFeature),
        ...(action === this.addNonTopoFeatureAction && {
          params: {
            layer_id: this.layer.id,
          },
        }),
      });
      this.$store.set('attributesSchema/DELETE_CACHED_RELATIONS_BY_DATASOURCE!', datasource);
      this.refreshTableAndMap();
      if (this.isDialogFeatureCard) {
        this.$emit('featureAdded', r.data.data);
      } else {
        await this.pushRouter(r.data.data);
      }
    },
    async addTopoFeature() {
      const datasource = this.hasTreeViewFeature ? this.treeViewLayerDataSource : this.layerDataSourceName;
      const properties = { ...this.values };
      delete properties.id;
      const action = this.customAddNewObjectAction || this.addTopoFeatureAction;
      const r = await this.addingHof(action, {
        datasource,
        body: {
          properties,
          geometry: this.geometry,
        },
        params: { layer_id: this.layerId },
      });
      this.refreshLayersAndTable(r.data.data.layers_id);
      if (this.isDialogFeatureCard) {
        this.$emit('featureAdded', r.data.data);
      } else {
        await this.pushRouter(r.data.data.inserted);
      }
    },
    async editingHof(fn, payload) {
      const r = await this.editingAddingHof(fn, payload, this.$i18n.t('success.successEditObjects'));
      this.refreshTableAndMap();
      if (this.isDialogFeatureCard) {
        this.$emit('featureEdited', r.data.data);
      } else {
        this.updateAfterEdit();
      }
      return r;
    },
    async editNonTopoFeature() {
      const datasource = this.hasTreeViewFeature ? this.treeViewLayerDataSource : this.layerDataSourceName;
      const id = this.hasTreeViewFeature ? this.treeViewFeatureId : this.featureId;
      await this.editingHof(this.editNonTopoFeatureAction, {
        datasource,
        id,
        body: this.getPayload(),
        params: { layer_id: this.layerId },
      });
      this.geometryChanged = false;
    },
    async editTopoFeature() {
      const datasource = this.hasTreeViewFeature ? this.treeViewLayerDataSource : this.layerDataSourceName;
      const properties = { ...this.values };
      delete properties.id;
      const r = await this.editingHof(this.editTopoFeatureAction, {
        datasource,
        id: this.featureId,
        body: {
          properties,
          ...(this.geometryChanged ? { geometry: this.geometry } : {}),
          created: this.newTopoIndexes,
          deleted: this.deletedTopoIndexes,
        },
        params: { layer_id: this.layerId },
      });
      this.refreshLayersAndTable(r.data.data);
      this.geometryChanged = false;
    },
    deleteObjectInit() {
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    openZSIDialog() {
      this.isZSIDialogLoaded = true;
      this.isZSIDialogVisible = true;
    },
    async deleteObjectHof(fn) {
      try {
        this.agreeButtonLoading = true;
        this.stopEditing();
        const { layerDataSourceName, featureId } = this;
        const r = this.isTopo
          ? await fn({
              datasource: layerDataSourceName,
              payload: { to_delete: [featureId] },
              params: { layer_id: this.layer.id },
            })
          : await fn({ datasource: layerDataSourceName, id: featureId, params: { layer_id: this.layer.id } });
        this.refreshTableAndMap();
        if (this.isDialogFeatureCard) {
          this.$emit('featureDeleted');
        } else {
          this.pushToParentRoute();
        }
        this.agreeButtonLoading = false;
        this.isDeleteDialogVisible = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successDeleteObject'),
        });
        return r;
      } catch (error) {
        this.agreeButtonLoading = false;
        this.isDeleteDialogVisible = false;
      }
    },
    getPayload(addTreeViewElement = false) {
      const geometryPropertyName = this.hasTreeViewFeature
        ? this.treeViewGeometryPropertyName
        : this.geometryPropertyName;
      return {
        ...this.values,
        ...(this.isGeometricalLayer && !this.geometryReadOnly && (this.geometryChanged || this.isNew)
          ? {
              [geometryPropertyName]: addTreeViewElement
                ? { ...this.treeViewFeature.geometry, crs: this.treeViewFeature.crs }
                : this.geometry,
            }
          : {}),
      };
    },
    refreshTableAndMap() {
      this.$root.$emit('refreshTableData');
      this.$root.$emit('refreshMvtSource', this.layerId);
      this.$root.$emit('updateLegendStylesCounts', { layersIds: [this.layerId] });
    },
    setAttributesValues(payload) {
      if (this.isFetched) {
        this.values = { ...this.values, ...payload };
        this.additionalAttributesValues = undefined;
      } else {
        this.additionalAttributesValues = payload;
      }
    },
    updateMeasure() {
      if (this.isMeasureFeatureActive) {
        this.toggleFeatureMeasurement(false);
        this.$nextTick(() => {
          this.toggleFeatureMeasurement(true);
        });
      }
    },
    async toggleFeatureMeasurement(value = !this.isMeasureFeatureActive, skipStatusChange = false) {
      if (value) {
        try {
          this.measureFeatureLoading = true;
          const feature = await this.getLayerFeature({
            layer_id: this.layerId,
            feature_id: this.featureId,
          });
          const geometry = { ...feature.geometry, crs: feature.crs };
          this.$store.set('map/ADD_SELECTED_FEATURES!', {
            type: 'measuredFeatures',
            features: {
              [this.layerId]: { [this.featureId]: { geometry } },
            },
          });
        } finally {
          this.measureFeatureLoading = false;
        }
      } else {
        this.$store.set('map/DELETE_SELECTED_FEATURES!', {
          type: 'measuredFeatures',
          features: {
            [this.layerId]: [this.featureId],
          },
        });
      }
      if (!skipStatusChange) {
        this.isMeasureFeatureActive = value;
      }
    },
    toggleListeners(isOn) {
      const method = isOn ? '$on' : '$off';
      if (!this.isDialogFeatureCard) {
        this.$root[method]('featureAdding', this.handleFeatureAdding);
        this.$root[method]('featureEditing', this.handleFeatureEditing);
      }
      this.$root[method]('cancelEditing', this.cancelEditing);
      this.$root[method]('turnOffEditing', this.stopEditing);
      this.$root[method]('turnOnEditing', () => this.startEditing('edit'));
      this.$root[method]('toggleMultipartFeatureManagement', this.multipartFeatureManagement);
      this.$root[method]('tableDataFetched', this.onLidFidChanged);
      this.$root[method]('refreshFormData', this.refreshFormData);
      this.$root[method]('openStopEditingDialog', this.openStopEditingDialog);
    },
    onValuesInput(values) {
      this.values = values;
    },
    editButtonClickHandler() {
      this.isEditingOn
        ? this.hasUnsavedChanges
          ? this.openStopEditingDialog()
          : this.cancelEditing()
        : this.startEditing('edit');
    },
    async init() {
      if (this.isExternalSystemsEnabled) await this.checkDatasourceConnection(this.layerDataSourceName);
      if (
        !this.formPrints.length &&
        this.isFormPrintSuperAdminEnabled &&
        this.isFormPrintAdminEnabled &&
        !!this.isFormPrintPermission
      ) {
        await this.getFormPrints();
      }
    },
  },
  created() {
    this.$watch(
      vm => {
        return {
          isEnterPressed: vm.isEnterPressed,
          isKeyBindingActive: vm.isKeyBindingActive,
          modalOverlayLevel: vm.modalOverlayLevel,
          isStickyButtonsVisible: vm.isStickyButtonsVisible,
        };
      },
      nV => {
        const { isStickyButtonsVisible, isEnterPressed, isKeyBindingActive, modalOverlayLevel } = nV;
        if (isStickyButtonsVisible && isEnterPressed && !isKeyBindingActive && modalOverlayLevel <= 0) {
          this[this.isNew ? 'handleFeatureAdding' : 'handleFeatureEditing']();
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
    this.onLidFidChanged = debounce(this.onLidFidChanged, 100);
  },
  mounted() {
    this.toggleListeners(true);
    this.init();
  },
  beforeDestroy() {
    this.toggleListeners(false);
    this.clearHighlitedFeature(this.layerId);
    this.stopEditing();
  },
};
</script>
